import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MultiStepForm from '../../ui-components/base/MultiStepForm';
import BannerInformationStep from './steps/BannerInformationStep';
import TranslationStep from './steps/TranslationStep';
import CountryTargetingStep from './steps/CountryTargetingStep';
import PreviewPublishStep from './steps/PreviewPublishStep';
import Notification from '../../components/shared/notification';
import { isLiveBannerLimitError, LiveBannerLimitContext } from './liveBannerLimit';
import BannerService from '../../service/BannerService';
import { setNotificationData } from '../../redux/actions/uiActions';
import homeIcon from '../../styles/images/gray_home_icon.svg';
import homeSelectedIcon from '../../styles/images/selected_home_icon.svg';
import translationIcon from '../../styles/images/translation.svg';
import translationSelectedIcon from '../../styles/images/translation_selected.svg';
import countryTargettingIcon from '../../styles/images/user_gray_icon.svg';
import countryTargettingSelectedIcon from '../../styles/images/users_icon.svg';
import reviewPublishIcon from '../../styles/images/spec_gray.svg';
import reviewPublishSelectedIcon from '../../styles/images/publish-review-selected.svg';
import styles from '../../styles/pages/Home.module.css';

/**
 * @typedef {Object} BannerTranslation
 * @property {string} locale - Secondary locale code (e.g., 'es-us'). Must not be the primary locale.
 * @property {string} title - Translated banner title
 * @property {string} description - Translated banner description
 * @property {string} [primary_button_text] - Translated primary button text (required if banner_type === 'button_link')
 * @property {string} [secondary_button_text] - Translated secondary button text (optional)
 * @property {string} image_description - Translated image description, max 100 chars (required for all banner types)
 *
 * Note: Links (primary_link, secondary_link) are NOT localized and should only be in top-level fields
 */

const IMAGE_DESCRIPTION_MAX_LENGTH = 100;

const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return obj1 === obj2;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => keys2.includes(key) && deepEqual(obj1[key], obj2[key]));
};

/**
 * Reduces step data to the values the user can actually change, so that data the steps
 * derive on their own (the auto-filled primary-locale translation, the full country objects
 * pushed by CountryTargetingStep) is not mistaken for an edit.
 */
const normalizeStepDataForCompare = (allStepData = {}, primaryLocale) => {
  const normalized = {};
  Object.keys(allStepData).forEach((stepIndex) => {
    const data = allStepData[stepIndex] || {};
    if (data.translations) {
      const translations = {};
      Object.keys(data.translations)
        .filter((locale) => locale !== primaryLocale)
        .forEach((locale) => { translations[locale] = data.translations[locale]; });
      normalized[stepIndex] = { ...data, translations };
    } else if (data.selectedCountries) {
      normalized[stepIndex] = {
        ...data,
        selectedCountries: data.selectedCountries.map((c) => c.countryCode).sort(),
      };
    } else {
      normalized[stepIndex] = data;
    }
  });
  return normalized;
};

const BannerCreationWizard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { bannerId } = useParams();

  // Get notification state from Redux
  const notificationData = useSelector((state) => state.uiState?.notificationData);
  const countriesData = useSelector((state) => state.commonData?.countries);
  const marketLanguagesData = useSelector((state) => state.commonData?.marketLanguages);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialStepData, setInitialStepData] = useState(null);
  // Current banner state fetched from the API (edit mode). Defaults to 'Draft' when not fetched.
  const [bannerState, setBannerState] = useState('Draft');
  const remoteConfig = useSelector((state) => state.uiState.remoteConfig);

  // Whether the user has changed anything compared to the loaded/initial step data.
  const [isDirty, setIsDirty] = useState(false);
  // banner_type as loaded from the API vs. the value currently selected in step 0 (edit mode).
  const [initialBannerType, setInitialBannerType] = useState(null);
  const [currentBannerType, setCurrentBannerType] = useState(null);

  // Snapshot of step data taken right after load (edit mode) or on first touch (create mode),
  // used by the header Back button to detect unsaved changes before navigating away.
  const baselineStepDataRef = useRef(null);
  const liveStepDataRef = useRef({});

  const isEditMode = Boolean(bannerId);

  // Set when the publish API rejects the request because the Live banner threshold is reached
  const [hasReachedLiveLimit, setHasReachedLiveLimit] = useState(false);

  // Fetch banner data in edit mode
  useEffect(() => {
    const fetchBannerData = async () => {
      if (!isEditMode) return;

      setIsLoading(true);
      try {
        const response = await BannerService.getBannerById(bannerId);

        if (response.status === 200 && response.data?.data) {
          const bannerData = response.data.data;

          // Helper to convert API image object to File (for preview)
          const imageToFilePreview = async (imageObj, deviceType) => {
            if (!imageObj?.href) return null;

            // Store the URL and metadata for later use
            return {
              href: imageObj.href,
              title: imageObj.title,
              isExisting: true, // Flag to indicate this is an existing image, not a new upload
            };
          };

          // Map API response translations to form state
          // API Response format: Array of { locale, title, description, imageDescription, primaryButtonText?, secondaryButtonText? }
          // Form format: Object keyed by locale with camelCase fields
          const translationsObj = {};
          if (bannerData.translations && Array.isArray(bannerData.translations)) {
            bannerData.translations.forEach(trans => {
              translationsObj[trans.locale] = {
                locale: trans.locale,
                title: trans.title || '',
                description: trans.description || '',
                primaryButtonText: trans.primaryButtonText || '',
                secondaryButtonText: trans.secondaryButtonText || '',
                imageDescription: trans.imageDescription || '',
                link: '', // Links are not localized
              };
            });
          }

          // Map API response to form state
          const mappedData = {
            // Step 0: Banner Information
            bannerInformationData: {
              bannerType: bannerData.bannerType === 'button_link' ? 'button' : 'image',
              title: bannerData.title || '',
              description: bannerData.description || '',
              link: bannerData.bannerType === 'image_link' ? (bannerData.primaryLink?.href || '') : '',
              imageDescription: bannerData.imageDescription || '',
              primaryButtonText: bannerData.primaryButtonText || '',
              primaryButtonLink: bannerData.bannerType === 'button_link' ? (bannerData.primaryLink?.href || '') : '',
              secondaryButtonText: bannerData.secondaryButtonText || '',
              secondaryButtonLink: bannerData.secondaryLink?.href || '',
              // Applies to both banner types; defaults to white when the API has no value
              buttonColor: bannerData.buttonTextColor === 'Black' ? 'black' : 'white',
              desktopBannerImage: await imageToFilePreview(bannerData.bannerImageDesktop, 'desktop'),
              mobileBannerImage: await imageToFilePreview(bannerData.bannerImageMobile, 'mobile'),
            },
            // Step 1: Translation - map from API response
            translationData: {
              translations: translationsObj,
            },
            // Step 2: Country Targeting
            countryTargetingData: {
              selectedCountries: (bannerData.countriesAvailability || '')
                .split(',')
                .filter(code => code.trim())
                .map(code => {
                  // Find country object from countriesData
                  const country = countriesData?.find(c => c.countryCode === code.trim());
                  return country || { countryCode: code.trim(), countryName: code.trim() };
                }),
            },
            bannerState: bannerData.bannerState || 'Draft',
          };

          const hydratedStepData = [
            mappedData.bannerInformationData,
            mappedData.translationData,
            mappedData.countryTargetingData,
          ];

          setInitialStepData(hydratedStepData);
          // Baseline snapshot for the "unsaved changes" check on the header Back button.
          // Keyed by step index to match the shape MultiStepForm reports via onStepDataChange.
          baselineStepDataRef.current = JSON.parse(JSON.stringify({
            0: hydratedStepData[0],
            1: hydratedStepData[1],
            2: hydratedStepData[2],
          }));

          setBannerState(mappedData.bannerState);
          setInitialBannerType(mappedData.bannerInformationData.bannerType);
          setCurrentBannerType(mappedData.bannerInformationData.bannerType);
          setIsDirty(false);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);

        const status = error.response?.status;
        let errorMessage = 'Failed to load banner data. Please try again.';

        if (status === 404) {
          errorMessage = 'Banner not found.';
        } else if (status === 401 || status === 403) {
          errorMessage = 'You do not have permission to edit this banner.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        dispatch(setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Error Loading Banner',
          description: errorMessage,
        }));

        // Redirect back to carousel-listing after showing error
        setTimeout(() => {
          history.push('/carousel-listing');
        }, 2000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBannerData();
  }, [isEditMode, bannerId, dispatch, history, countriesData]);

  // Define steps configuration (removed title to prevent duplication)
  // IMPORTANT: useMemo prevents steps array from being recreated on every render
  // Without this, MultiStepForm's useEffect would reset all step validations to false on every render
  const steps = useMemo(() => [
    {
      title: 'Add Banner Details',
      label: 'Banner Information',
      icon: {
        selected: homeSelectedIcon,
        unselected: homeIcon
      },
      component: <BannerInformationStep isLoading={isLoading} />,
    },
    {
      // title: 'Translation',
      // titleDescription: 'Add multiple language support to this Banner by adding a translation for the Title, Description and Button Text (if applicable).',
      label: 'Translation',
      icon: {
        selected: translationSelectedIcon,
        unselected: translationIcon
      },
      component: <TranslationStep bannerId={bannerId} isEditMode={isEditMode} />,
    },
    remoteConfig?.user_group_country_targeting ? {
      // title: 'Country Targeting',
      label: 'Country Targeting',
      icon: {
        selected: countryTargettingSelectedIcon,
        unselected: countryTargettingIcon
      },
      component: <CountryTargetingStep isEditMode={isEditMode} />,
    } : null,
    {
      // title: 'Preview & Publish',
      label: 'Preview & Publish',
      icon: {
        selected: reviewPublishSelectedIcon,
        unselected: reviewPublishIcon
      },
      component: <PreviewPublishStep />,
    },
  ].filter(s => s?.component), [isLoading]); // Include isLoading in dependencies

  /**
   * Validates banner data before submission
   * @param {Object} bannerData - Banner information from step 0
   * @param {Object} countryData - Country targeting from step 2
   * @param {Object} translationData - Translation data from step 1
   * @returns {Array<string>} Array of error messages
   */
  const validateBannerData = (bannerData, countryData, translationData = {}) => {
    const errors = [];
    const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB in bytes
    const URL_MAX_LENGTH = 2048;
    const URL_PATTERN = /^https?:\/\/.+/i;
    // Relative URLs (e.g. /abc/xyz or /abc?id=xyz) are also allowed, as long as they start with /
    const isValidLink = (url) => URL_PATTERN.test(url) || url.startsWith('/');

    // File size validation
    if (bannerData.desktopBannerImage && bannerData.desktopBannerImage.size > MAX_FILE_SIZE) {
      errors.push('Desktop banner image must be less than 6MB');
    }
    if (bannerData.mobileBannerImage && bannerData.mobileBannerImage.size > MAX_FILE_SIZE) {
      errors.push('Mobile banner image must be less than 6MB');
    }

    // Link validations based on banner type
    if (bannerData.bannerType === 'button') {
      // Button link mode validations
      if (!bannerData.primaryButtonText || !bannerData.primaryButtonText.trim()) {
        errors.push('Primary button text is required for Button Link mode');
      }
      if (!bannerData.primaryButtonLink || !bannerData.primaryButtonLink.trim()) {
        errors.push('Primary button link is required for Button Link mode');
      }
      if (bannerData.primaryButtonLink && !isValidLink(bannerData.primaryButtonLink)) {
        errors.push('Primary button link must be a valid HTTP(S) URL or a relative URL starting with /');
      }
      if (bannerData.primaryButtonLink && bannerData.primaryButtonLink.length > URL_MAX_LENGTH) {
        errors.push(`Primary button link must be less than ${URL_MAX_LENGTH} characters`);
      }
      if (bannerData.secondaryButtonLink && !isValidLink(bannerData.secondaryButtonLink)) {
        errors.push('Secondary button link must be a valid HTTP(S) URL or a relative URL starting with /');
      }
      if (bannerData.secondaryButtonLink && bannerData.secondaryButtonLink.length > URL_MAX_LENGTH) {
        errors.push(`Secondary button link must be less than ${URL_MAX_LENGTH} characters`);
      }
    } else {
      // Image link mode validations
      if (bannerData.link && bannerData.link.trim()) {
        if (!isValidLink(bannerData.link)) {
          errors.push('Link must be a valid HTTP(S) URL or a relative URL starting with /');
        }
        if (bannerData.link.length > URL_MAX_LENGTH) {
          errors.push(`Link must be less than ${URL_MAX_LENGTH} characters`);
        }
      }
    }

    // Image description is required for both modes
    if (!bannerData.imageDescription || !bannerData.imageDescription.trim()) {
      errors.push('Image description is required');
    } else if (bannerData.imageDescription.length > IMAGE_DESCRIPTION_MAX_LENGTH) {
      errors.push(`Image description must be ${IMAGE_DESCRIPTION_MAX_LENGTH} characters or less`);
    }

    // Country availability validation
    if (remoteConfig?.user_group_country_targeting && (!countryData.selectedCountries || countryData.selectedCountries.length === 0)) {
      errors.push('At least one country must be selected');
    }

    // Translation validations (skipped for Image Link banners with no title and description)
    const hasTranslatableContent = bannerData.bannerType === 'button'
      || !!(bannerData.title || '').trim()
      || !!(bannerData.description || '').trim();
    if (hasTranslatableContent && translationData.translations && typeof translationData.translations === 'object') {
      const primaryLocale = marketLanguagesData?.marketDefaultLocale?.locale;
      const translationLocales = Object.keys(translationData.translations);

      // Filter out primary locale (it's stored in translations for display but excluded from API payload)
      const secondaryLocales = translationLocales.filter(locale => locale !== primaryLocale);

      // Check for duplicate locales (excluding primary)
      const uniqueLocales = new Set(secondaryLocales);
      if (uniqueLocales.size !== secondaryLocales.length) {
        errors.push('Duplicate locales found in translations');
      }

      // Validate each secondary locale translation
      secondaryLocales.forEach(locale => {
        const trans = translationData.translations[locale];

        // Title is required only when the banner itself has a title
        const bannerHasTitle = !!(bannerData.title && bannerData.title.trim());
        if (bannerHasTitle && (!trans.title || trans.title.trim().length === 0)) {
          errors.push(`Translation for ${locale}: Title is required`);
        }

        // Validate button text for button_link mode
        if (bannerData.bannerType === 'button') {
          if (!trans.primaryButtonText || trans.primaryButtonText.trim().length === 0) {
            errors.push(`Translation for ${locale}: Primary button text is required for Button Link mode`);
          }
        }

        // Image description is required for both modes
        if (!trans.imageDescription || !trans.imageDescription.trim()) {
          errors.push(`Translation for ${locale}: Image description is required`);
        } else if (trans.imageDescription.length > IMAGE_DESCRIPTION_MAX_LENGTH) {
          errors.push(`Translation for ${locale}: Image description must be ${IMAGE_DESCRIPTION_MAX_LENGTH} characters or less`);
        }
      });
    }

    return errors;
  };

  // Build FormData from wizard steps
  const buildFormData = (allStepData, bannerState = 'Live') => {
    const bannerData = allStepData[0] || {};
    const translationData = allStepData[1] || {};
    const countryData = allStepData[2] || {};

    const formData = new FormData();

    // Basic fields
    formData.append('title', (bannerData.title || '').trim());
    formData.append('description', (bannerData.description || '').trim());
    formData.append('banner_type', bannerData.bannerType === 'button' ? 'button_link' : 'image_link');

    // Images - only append if it's a new File object (not an existing image reference)
    if (bannerData.desktopBannerImage) {
      // Check if this is a new upload (File) or existing image (object with href)
      if (bannerData.desktopBannerImage instanceof File) {
        formData.append('banner_image_desktop', bannerData.desktopBannerImage);
        formData.append('banner_image_desktop_title', bannerData.desktopBannerImage.name || 'desktop-banner');
      } else if (bannerData.desktopBannerImage.isExisting && !isEditMode) {
        // In create mode, if we somehow have an existing image reference, skip it
        // In edit mode, we don't append anything to keep the existing image
      }
    }
    if (bannerData.mobileBannerImage) {
      if (bannerData.mobileBannerImage instanceof File) {
        formData.append('banner_image_mobile', bannerData.mobileBannerImage);
        formData.append('banner_image_mobile_title', bannerData.mobileBannerImage.name || 'mobile-banner');
      }
    }

    // Link and Button fields based on banner type
    if (bannerData.bannerType === 'button') {
      // Button Link mode
      formData.append('primary_button_text', (bannerData.primaryButtonText || '').trim());
      formData.append('primary_link', (bannerData.primaryButtonLink || '').trim());

      if (bannerData.secondaryButtonText && bannerData.secondaryButtonText.trim()) {
        formData.append('secondary_button_text', bannerData.secondaryButtonText.trim());
      }
      if (bannerData.secondaryButtonLink && bannerData.secondaryButtonLink.trim()) {
        formData.append('secondary_link', bannerData.secondaryButtonLink.trim());
      }
    } else {
      // Image Link mode
      if (bannerData.link && bannerData.link.trim()) {
        formData.append('primary_link', bannerData.link.trim());
      }
    }

    // Image description is sent for both modes
    formData.append('image_description', (bannerData.imageDescription || '').trim());

    // Button text color (exact casing) - sent for both banner types
    formData.append('button_text_color', bannerData.buttonColor === 'white' ? 'White' : 'Black');

    // Countries availability (comma-separated string).
    // If CountryTargetingStep was never visited (e.g. Save & Exit from Banner Information), fall
    // back to that step's default selection: every available country from Redux.
    const selectedCountries = countryData.selectedCountries
      || (remoteConfig?.user_group_country_targeting ? (countriesData || []) : []);
    const countryCodes = selectedCountries
      .map(c => c.countryCode)
      .join(',');
    formData.append('countries_availability', countryCodes);

    // Banner state (exact casing)
    formData.append('banner_state', bannerState); // 'Draft', 'Live', or 'Offline'

    // Translations - convert translations object to API format
    // Format: Array of { locale, title, description, image_description, primary_button_text?, secondary_button_text? }
    // Exclude primary locale from translations (it's already in the top-level fields)
    // Image Link banner without title and description has nothing to translate
    const isNoTranslationNeeded = bannerData.bannerType !== 'button'
      && !(bannerData.title || '').trim()
      && !(bannerData.description || '').trim();

    if (!isNoTranslationNeeded && translationData.translations && typeof translationData.translations === 'object') {
      // Get primary locale from market languages
      const primaryLocale = marketLanguagesData?.marketDefaultLocale?.locale;

      const translationsArray = Object.keys(translationData.translations)
        .filter(locale => {
          // Exclude the primary locale - it should not be in translations
          // Primary locale data is already in top-level fields (title, description, etc.)
          return locale !== primaryLocale;
        })
        .map(locale => {
          const trans = translationData.translations[locale];

          // Build translation object according to API spec
          const translationObj = {
            locale: trans.locale,
            title: (trans.title || '').trim(),
            description: trans.description ? trans.description.trim() : '',
            image_description: (trans.imageDescription || '').trim(),
          };

          // Add button text fields only if banner_type is button_link
          if (bannerData.bannerType === 'button') {
            if (trans.primaryButtonText && trans.primaryButtonText.trim()) {
              translationObj.primary_button_text = trans.primaryButtonText.trim();
            }
            if (trans.secondaryButtonText && trans.secondaryButtonText.trim()) {
              translationObj.secondary_button_text = trans.secondaryButtonText.trim();
            }
          }

          return translationObj;
        });

      // Only append translations if there are secondary locale translations
      if (translationsArray.length > 0) {
        formData.append('translations', JSON.stringify(translationsArray));
      }
    }

    return formData;
  };

  // Handle form submission (final step - Publish as Live)
  const handleSubmit = async (allStepData) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Convert allStepData object to array format
      const stepDataArray = Object.keys(allStepData)
        .sort((a, b) => Number(a) - Number(b))
        .map(key => allStepData[key]);

      const bannerData = stepDataArray[0] || {};
      const translationData = stepDataArray[1] || {};
      const countryData = stepDataArray[2] || {};

      // Client-side validation
      const validationErrors = validateBannerData(bannerData, countryData, translationData);
      if (validationErrors.length > 0) {
        console.log('[BannerWizard] Validation failed - Re-enabling buttons');
        dispatch(setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Validation Error',
          description: validationErrors.join('; '),
        }));
        // Re-enable buttons after notification
        setTimeout(() => {
          setIsSubmitting(false);
          console.log('[BannerWizard] Buttons re-enabled after validation error');
        }, 0);
        return;
      }

      // Build FormData for Live banner
      const formData = buildFormData(stepDataArray, 'Live');

      // Call API - use update or create based on mode
      const response = isEditMode
        ? await BannerService.updateBanner(bannerId, formData)
        : await BannerService.createBanner(formData);

      // Handle response based on status code
      if (response.status === 200 || response.status === 201) {
        // Success - Banner created/updated
        dispatch(setNotificationData({
          show: true,
          type: 'SUCCESS',
          title: isEditMode ? 'Banner Updated' : 'Banner Published',
          description: isEditMode ? 'Banner Updated Successfully' : 'New Banner Published Successfully',
        }));
        setTimeout(() => {
          history.push('/carousel-listing');
        }, 1500);
      } else if (response.status === 207) {
        // Multi-Status - Partial success (carousel sequence warning), show the API message
        dispatch(setNotificationData({
          show: true,
          type: 'WARNING',
          title: isEditMode ? 'Banner Updated with Warning' : 'Banner Published with Warning',
          description: response.data?.message || (isEditMode
            ? 'Banner updated successfully, but the carousel sequence could not be updated.'
            : 'Banner created successfully, but the carousel sequence could not be updated.'),
        }));
        setTimeout(() => {
          history.push('/carousel-listing');
        }, 1500);
      }
    } catch (error) {
      console.error('Error creating banner:', error);

      // Live banner threshold reached: stay on Preview & Publish with the limit alert and
      // Publish disabled, so the user can still Save & Exit as a draft
      if (isLiveBannerLimitError(error)) {
        setHasReachedLiveLimit(true);
        return;
      }

      // Parse error response
      const errorResponse = error.response?.data;
      const status = error.response?.status;

      let errorMessage = 'Failed to create banner. Please try again.';

      if (status === 400) {
        // Bad Request - parse message (array or string)
        const message = errorResponse?.message;
        if (Array.isArray(message)) {
          errorMessage = message.join('; ');
        } else if (typeof message === 'string') {
          errorMessage = message;
        }
      } else if (status === 401 || status === 403) {
        errorMessage = 'Authentication error. Please log in again.';
      } else if (status === 404) {
        errorMessage = 'API endpoint not found. Please contact support.';
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }

      // Show notification
      dispatch(setNotificationData({
        show: true,
        type: 'WARNING',
        title: isEditMode ? 'Failed to Update Banner' : 'Failed to Publish Banner',
        description: errorMessage,
      }));

      // Re-enable buttons after notification dispatch
      // Use setTimeout to ensure it happens in next tick after notification state update
      console.log('[BannerWizard] API Error - Re-enabling buttons');
      setTimeout(() => {
        setIsSubmitting(false);
        console.log('[BannerWizard] Buttons should now be enabled');
      }, 0);
    } finally {
      // Backup: ensure buttons are enabled after a small delay
      setTimeout(() => {
        setIsSubmitting(false);
      }, 100);
    }
  };

  // Handle save and exit (save as Draft)
  const handleSaveAndExit = async (allStepData, currentStep) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Convert allStepData object to array format
      const stepDataArray = Object.keys(allStepData)
        .sort((a, b) => Number(a) - Number(b))
        .map(key => allStepData[key]);

      const bannerData = stepDataArray[0] || {};
      const countryData = stepDataArray[2] || {};

      // Minimal validation for draft (only require essential fields); title is optional for Image Link
      if (bannerData.bannerType === 'button' && (!bannerData.title || !bannerData.title.trim())) {
        console.log('[BannerWizard] Draft validation failed (title) - Re-enabling buttons');
        dispatch(setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Validation Error',
          description: 'Title is required to save draft',
        }));
        setTimeout(() => {
          setIsSubmitting(false);
          console.log('[BannerWizard] Buttons re-enabled after validation error');
        }, 0);
        return;
      }

      if (!bannerData.desktopBannerImage) {
        console.log('[BannerWizard] Draft validation failed (image) - Re-enabling buttons');
        dispatch(setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Validation Error',
          description: 'Desktop banner image is required to save draft',
        }));
        setTimeout(() => {
          setIsSubmitting(false);
          console.log('[BannerWizard] Buttons re-enabled after validation error');
        }, 0);
        return;
      }

      // Save & Exit preserves the banner's current state (fetched from the API).
      // Falls back to 'Draft' when the state was not fetched (e.g. create mode).
      const formData = buildFormData(stepDataArray, bannerState || 'Draft');

      // Call API - use update or create based on mode
      const response = isEditMode
        ? await BannerService.updateBanner(bannerId, formData)
        : await BannerService.createBanner(formData);

      // Handle response
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotificationData({
          show: true,
          type: 'SUCCESS',
          title: isEditMode ? 'Banner Updated Successfully' : 'Banner saved as draft successfully',
          description: '',
        }));
        setTimeout(() => {
          history.push('/carousel-listing');
        }, 1500);
      } else if (response.status === 207) {
        dispatch(setNotificationData({
          show: true,
          type: 'WARNING',
          title: 'Draft Saved with Warning',
          description: response.data?.message || 'Banner saved as draft, but carousel sequence could not be updated.',
        }));
        setTimeout(() => {
          history.push('/carousel-listing');
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving draft:', error);

      const errorResponse = error.response?.data;
      const status = error.response?.status;

      let errorMessage = 'Failed to save draft. Please try again.';

      if (status === 400) {
        const message = errorResponse?.message;
        if (Array.isArray(message)) {
          errorMessage = message.join('; ');
        } else if (typeof message === 'string') {
          errorMessage = message;
        }
      } else if (status === 401 || status === 403) {
        errorMessage = 'Authentication error. Please log in again.';
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }

      // Show notification
      dispatch(setNotificationData({
        show: true,
        type: 'WARNING',
        title: 'Failed to Save Draft',
        description: errorMessage,
      }));

      // Re-enable buttons after notification dispatch
      // Use setTimeout to ensure it happens in next tick after notification state update
      console.log('[BannerWizard] API Error - Re-enabling buttons');
      setTimeout(() => {
        setIsSubmitting(false);
        console.log('[BannerWizard] Buttons should now be enabled');
      }, 0);
    } finally {
      // Backup: ensure buttons are enabled after a small delay
      setTimeout(() => {
        setIsSubmitting(false);
      }, 100);
    }
  };

  // Handle step change (optional callback)
  const handleStepChange = (newStep, stepConfig) => {
    console.log('Navigated to step:', newStep, stepConfig.label);
  };

  const primaryLocaleCode = marketLanguagesData?.marketDefaultLocale?.locale;

  // Compares the live step data against the baseline snapshot, ignoring data the steps derive on their own
  const hasUnsavedChanges = useCallback(() => !deepEqual(
    normalizeStepDataForCompare(baselineStepDataRef.current || {}, primaryLocaleCode),
    normalizeStepDataForCompare(liveStepDataRef.current || {}, primaryLocaleCode),
  ), [primaryLocaleCode]);

  // Handle header Back button - warn if the user has unsaved changes
  const handleHeaderBack = () => {
    if (hasUnsavedChanges()) {
      const confirmLeave = window.confirm('You have unsaved changes. Do you really want to leave?');
      if (!confirmLeave) return;
    }

    history.push('/carousel-listing');
  };

  const handleStepDataChange = useCallback((latestStepData) => {
    // Each step component pushes its own default field values up as soon as it mounts,
    // before the user touches anything. The first time a given step's data shows up here,
    // treat it as that step's "untouched" baseline (unless a baseline was already fetched
    // for it, e.g. edit mode) instead of flagging those defaults as unsaved changes.
    if (!baselineStepDataRef.current) {
      baselineStepDataRef.current = {};
    }
    Object.keys(latestStepData || {}).forEach((stepIndex) => {
      if (!(stepIndex in baselineStepDataRef.current)) {
        baselineStepDataRef.current[stepIndex] = JSON.parse(JSON.stringify(latestStepData[stepIndex]));
      }
    });
    liveStepDataRef.current = latestStepData;

    setCurrentBannerType(latestStepData?.[0]?.bannerType || null);
    // In edit mode the baseline is only meaningful once the banner has been fetched
    if (!isEditMode || initialStepData) {
      setIsDirty(hasUnsavedChanges());
    }
  }, [isEditMode, initialStepData, hasUnsavedChanges]);

  // Re-evaluate once the primary locale is known, since it changes what counts as an edit
  useEffect(() => {
    if (!isEditMode || initialStepData) {
      setIsDirty(hasUnsavedChanges());
    }
  }, [hasUnsavedChanges]);

  // Edit mode, untouched form: show "Cancel" (navigate away, no API call) instead of "Save & Exit"
  const showCancel = isEditMode && !isDirty;

  // Live banners can jump to any step, unless banner_type was switched away from its loaded value
  // (the later steps depend on it, so they must be reached through validation again)
  const unlockAllSteps = isEditMode
    && bannerState === 'Live'
    && Boolean(initialBannerType)
    && currentBannerType === initialBannerType;

  // Optional: Custom validation per step (in addition to built-in validation)
  const handleValidateStep = async (stepIndex, stepData) => {
    console.log('Validating step:', stepIndex, 'with data:', stepData);

    // Example: Add custom async validation
    // if (stepIndex === 0) {
    //   // Validate banner title uniqueness via API
    //   if (stepData?.title) {
    //     // const response = await fetch(`/api/banners/validate-title?title=${stepData.title}`);
    //     // if (!response.ok) {
    //     //   alert('A banner with this title already exists');
    //     //   return false;
    //     // }
    //   }
    // }

    return true; // Return true if valid
  };

  return (
    <div className="page_content">
      <div className={styles.container}>
        <main className={styles.main_fixed}>
          <LiveBannerLimitContext.Provider value={hasReachedLiveLimit}>
          <MultiStepForm
            title={isEditMode ? "Edit Banner" : "Add Banner"}
            steps={steps}
            initialStep={0}
            initialStepData={initialStepData}
            onSubmit={handleSubmit}
            onSaveAndExit={handleSaveAndExit}
            onStepChange={handleStepChange}
            validateStep={handleValidateStep}
            saveButtonText="Save & Exit"
            nextButtonText="Next"
            submitButtonText={isEditMode ? "Update Banner" : "Publish"}
            showBackButton={false}
            isSubmitting={isSubmitting}
            showHeaderBackButton={true}
            onHeaderBack={handleHeaderBack}
            onStepDataChange={handleStepDataChange}
            exitStepIndex={0}
            exitButtonText={showCancel ? "Cancel" : "Exit"}
            onExit={() => history.push('/carousel-listing')}
            forcePlainExit={showCancel}
            unlockAllSteps={unlockAllSteps}
            submitDisabled={hasReachedLiveLimit}
          />
          </LiveBannerLimitContext.Provider>
        </main>
      </div>
      {/* Notification Component for error/success messages */}
      {notificationData?.show && <Notification />}
    </div>
  );
};

export default BannerCreationWizard;
